import { useEffect, useRef } from 'react';

/**
 * Call `handler` when a single character key is pressed at the document
 * level. Intentionally narrow — single printable keys without modifiers
 * (no Ctrl/Cmd/Alt/Shift combos). For shortcuts like Cmd+K, build a
 * separate hook; the shape of "ignore typing" differs.
 *
 * Skips the handler when:
 *   - the event was already consumed (defaultPrevented)
 *   - focus is inside an input / textarea / contenteditable — so single
 *     letters like `s` don't hijack a user typing in a form
 *   - a modal dialog / alertdialog is open — those components own the
 *     keyboard. We intentionally do NOT skip when only a Radix menu is
 *     open: dropdown menus mirror their item shortcuts (e.g. "S" next
 *     to "Star") and the user expects pressing that letter while the
 *     menu is visible to fire the action.
 *   - `enabled` is false (caller can gate by active tab / feature flag)
 *
 * `scope` is the element the hotkey belongs to. A dialog that CONTAINS it
 * does not block the hotkey — a panel hosted inside a sheet keeps its
 * shortcuts — while any other open dialog (one opened over it) still does.
 *
 * `key` is case-insensitive. Uses a ref for the handler so a closure
 * update doesn't require re-binding the document listener.
 */
export function useHotkey(
  key: string,
  handler: () => void,
  enabled: boolean = true,
  scope?: React.RefObject<HTMLElement | null>
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    const lowered = key.toLowerCase();

    const onKeyDown = (e: KeyboardEvent): void => {
      if (!enabledRef.current) return;
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.toLowerCase() !== lowered) return;

      // Don't steal keypresses meant for a text field.
      const target = e.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
      }

      // Don't fire while a modal dialog owns the keyboard — the user
      // expects their keys to flow to the open dialog, not to
      // background actions. We deliberately do NOT block on
      // `[role='menu']`: dropdown menus mirror their item shortcuts
      // (e.g. "S" next to "Star") and the user expects pressing that
      // letter while the menu is open to fire the action and dismiss
      // the menu. Handlers are responsible for closing the menu.
      const owner = scope?.current ?? null;
      const openOverlays = document.querySelectorAll(
        "[role='dialog'][data-state='open']," + "[role='alertdialog'][data-state='open']"
      );
      for (const overlay of openOverlays) {
        if (!owner || !overlay.contains(owner)) return;
      }

      e.preventDefault();
      handlerRef.current();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [key]);
}
